// internal-transfer-tasks-panel.tsx

import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  List,
  ListItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import RestoreIcon from "@mui/icons-material/Restore";
import SaveIcon from "@mui/icons-material/Save";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import { OrderType } from "../../../interfaces/delivery-company/orders";
import { IInternalTransferTask } from "../../../interfaces/delivery-company/tasks";
import { BranchType } from "../../../interfaces/delivery-company/branches";
import useInternalTransferTasks from "../../../hooks/use-internal-transfer-tasks";
import { useSnackBar } from "../../../contexts/snackbar";
import { InternalTransferTaskState } from "../../../enums/tasks";
import { DriverSelector } from "../../common/selectors/driver-selector";
import { DriverType } from "../../../interfaces/delivery-company/drivers";
import FilterChips from "../../common/filter-chips/filter-chips";
import { MerchantWithIcon } from "../../common/styles/merchant-with-icon";
import CustomerWithNavigate from "../../common/customer-with-navigate/customer-with-navigate";
import { showWorkflow } from "../../common/styles/workflow-display";
import { displayLocation } from "../../../utils/locations";
import { ReceiveTransferDialog } from "./receive-transfer-dialog";

interface InternalTransferTasksPanelProps {
  selectedOrders: Map<string, OrderType>;
  onSelectedBasketAddedToTask: () => void;
  onTaskDeleted?: () => void;
  onOrdersRemovedFromTask?: () => void;
  selectedBranch: BranchType | null;
  transferType: "Inbound" | "Outbound";
  currentBranch: BranchType | null;
}

export const InternalTransferTasksPanel: React.FC<
  InternalTransferTasksPanelProps
> = ({ selectedOrders, selectedBranch, onSelectedBasketAddedToTask, onTaskDeleted, onOrdersRemovedFromTask, transferType, currentBranch }) => {
  // Filter state for Active/Past tasks
  type TaskFilter = "Active" | "Past";
  const [selectedFilter, setSelectedFilter] = useState<TaskFilter[]>(["Active"]);

  // Driver filter state
  const [selectedDriverFilter, setSelectedDriverFilter] = useState<DriverType | null>(null);

  // Determine filterActive value for backend: true for active, false for past, null for both
  const filterActive = useMemo(() => {
    const showActive = selectedFilter.includes("Active");
    const showPast = selectedFilter.includes("Past");
    
    if (showActive && showPast) {
      return null; // Show both
    } else if (showActive) {
      return true; // Show only active
    } else if (showPast) {
      return false; // Show only past
    }
    return true; // Default to active
  }, [selectedFilter]);

  // For Inbound: show tasks where target is current branch (tasks coming TO us)
  // If sourceBranch is selected, filter by source branch as well
  // For Outbound: show tasks where source is current branch and target is selected branch (tasks going FROM us TO selected branch)
  const { tasks: tasksData, createTask, updateTask, deleteTask, isLoading, updatingTaskId, getTask } = useInternalTransferTasks({
    autoFetch: true,
    sourceBranchId: transferType === "Outbound" ? currentBranch?.id : selectedBranch?.id,
    targetBranchId: transferType === "Inbound" ? currentBranch?.id : selectedBranch?.id,
    filterActive,
    driverId: selectedDriverFilter?.id || null,
  });

  const { showMessage } = useSnackBar();

  // Ensure tasks is always an array
  const tasks = tasksData || [];

  // Track which task card is currently being hovered during drag
  const [dragOverTaskId, setDragOverTaskId] = useState<string | null>(null);
  
  // Track create task loading state
  const [isCreatingTask, setIsCreatingTask] = useState(false);
  
  // Track which task is being deleted
  const [deletingTaskId, setDeletingTaskId] = useState<string | null>(null);
  
  // Track which task is being assigned a driver
  const [assigningDriverTaskId, setAssigningDriverTaskId] = useState<string | null>(null);

  // Track which task's orders section is expanded
  const [expandedTaskId, setExpandedTaskId] = useState<string | null>(null);
  
  // Track receive dialog state
  const [receiveDialogTaskId, setReceiveDialogTaskId] = useState<string | null>(null);
  
  // Track fetched orders for each task and loading state
  const [taskOrders, setTaskOrders] = useState<Map<string, OrderType[]>>(new Map());
  const [loadingOrdersTaskId, setLoadingOrdersTaskId] = useState<string | null>(null);
  
  // Track pending removals per task (orders marked for removal but not yet saved)
  const [pendingRemovals, setPendingRemovals] = useState<Map<string, Set<string>>>(new Map());

  const selectedOrdersArray = useMemo(
    () => Array.from(selectedOrders.values()),
    [selectedOrders]
  );
  const selectedCount = selectedOrdersArray.length;

  // Check if Past filter is selected
  const isPastFilterSelected = selectedFilter.includes("Past") && !selectedFilter.includes("Active");

  const handleCreateTask = async () => {
    if (transferType === "Inbound") {
      showMessage("Cannot create tasks in Inbound mode.", "error");
      return;
    }
    
    if (!selectedBranch) {
      showMessage("Please select a target branch first.", "error");
      return;
    }
    
    setIsCreatingTask(true);
    try {
      console.log("Creating task with:", {
        target_branch: selectedBranch,
        orders: [],
      });

      // Create task with empty orders - orders will be added via drag and drop
      const result = await createTask({
        target_branch: selectedBranch, // selectedBranch is the target, send as object
        orders: [], // Empty orders array
      });

      if (!result) {
        // Error message already shown by createTask
        console.error("Failed to create task");
      }
    } finally {
      setIsCreatingTask(false);
    }
  };

  const handleAllowDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDropOnTask = async (
    e: React.DragEvent<HTMLDivElement>,
    taskId: string
  ) => {
    e.preventDefault();
    // clear highlight when dropped
    setDragOverTaskId(null);

    // Don't allow drops in Inbound mode
    if (transferType === "Inbound") return;

    if (!selectedOrdersArray.length) return;

    const task = tasks.find((t) => t.id === taskId);
    if (!task) return;

    const existingIds = new Set(task.orders.map((o) => o.order_id));
    const newOrderIds = selectedOrdersArray
      .filter((o) => !existingIds.has(o.order_id))
      .map((o) => o.order_id);

    if (newOrderIds.length === 0) return;

    // Combine existing order IDs with new ones
    const allOrderIds = [
      ...task.orders.map((o) => o.order_id),
      ...newOrderIds,
    ];

    await updateTask(taskId, {
      orders: allOrderIds,
    });
    // Clear any pending removals for this task since we're adding new orders
    setPendingRemovals((prev) => {
      const newMap = new Map(prev);
      newMap.delete(taskId);
      return newMap;
    });
    // If the orders section is expanded, refetch orders immediately
    if (expandedTaskId === taskId) {
      setLoadingOrdersTaskId(taskId);
      try {
        const fullTask = await getTask(taskId);
        if (fullTask && fullTask.orders) {
          setTaskOrders((prev) => new Map(prev).set(taskId, fullTask.orders));
        }
      } catch (error) {
        console.error("Failed to refetch task orders", error);
        // If refetch fails, clear cache so it will be refetched on next expand
        setTaskOrders((prev) => {
          const newMap = new Map(prev);
          newMap.delete(taskId);
          return newMap;
        });
      } finally {
        setLoadingOrdersTaskId(null);
      }
    } else {
      // Clear cached orders for this task so they'll be refetched when expanded
      setTaskOrders((prev) => {
        const newMap = new Map(prev);
        newMap.delete(taskId);
        return newMap;
      });
    }
    onSelectedBasketAddedToTask();
  };

  const handleDeleteTask = async (taskId: string) => {
    setDeletingTaskId(taskId);
    try {
      await deleteTask(taskId);
      // Call the callback to refetch orders and branch stats
      if (onTaskDeleted) {
        onTaskDeleted();
      }
    } finally {
      setDeletingTaskId(null);
    }
  };

  const handleAssignDriver = async (taskId: string, driver: DriverType | null) => {
    setAssigningDriverTaskId(taskId);
    try {
      await updateTask(taskId, {
        driver: driver,
      });
    } finally {
      setAssigningDriverTaskId(null);
    }
  };

  // Check if task can have driver assigned (only CREATED or ASSIGNED status)
  const canAssignDriver = (task: IInternalTransferTask) => {
    return task.status === InternalTransferTaskState.CREATED || 
           task.status === InternalTransferTaskState.ASSIGNED;
  };


  // NEW: when drag enters a card, mark it as active (only for Outbound)
  const handleDragEnterTask = (
    e: React.DragEvent<HTMLDivElement>,
    taskId: string
  ) => {
    e.preventDefault();
    e.stopPropagation();
    const task = tasks.find(t => t.id === taskId);
    if (transferType === "Inbound" || !selectedOrdersArray.length || !task || ![InternalTransferTaskState.CREATED, InternalTransferTaskState.ASSIGNED].includes(task.status)) return;
    setDragOverTaskId(taskId);
  };

  // NEW: when drag leaves a card, clear if it's the same one
  const handleDragLeaveTask = (
    e: React.DragEvent<HTMLDivElement>,
    taskId: string
  ) => {
    e.preventDefault();
    e.stopPropagation();
    // Only clear if we're actually leaving the card (not just moving to a child element)
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    
    // Check if mouse is actually outside the card boundaries
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setDragOverTaskId((current) => (current === taskId ? null : current));
    }
  };

  // Handle expanding/collapsing orders section
  const handleToggleOrders = async (taskId: string) => {
    if (expandedTaskId === taskId) {
      // Collapse
      setExpandedTaskId(null);
    } else {
      // Expand - fetch orders if not already fetched
      setExpandedTaskId(taskId);
      if (!taskOrders.has(taskId)) {
        setLoadingOrdersTaskId(taskId);
        try {
          const fullTask = await getTask(taskId);
          if (fullTask && fullTask.orders) {
            setTaskOrders((prev) => new Map(prev).set(taskId, fullTask.orders));
          }
        } catch (error) {
          console.error("Failed to fetch task orders", error);
        } finally {
          setLoadingOrdersTaskId(null);
        }
      }
    }
  };

  // Handle removing an order (mark for removal)
  const handleRemoveOrder = (taskId: string, orderId: string) => {
    setPendingRemovals((prev) => {
      const newMap = new Map(prev);
      const removals = newMap.get(taskId) || new Set<string>();
      removals.add(orderId);
      newMap.set(taskId, removals);
      return newMap;
    });
  };

  // Handle adding back a removed order
  const handleAddBackOrder = (taskId: string, orderId: string) => {
    setPendingRemovals((prev) => {
      const newMap = new Map(prev);
      const removals = newMap.get(taskId);
      if (removals) {
        removals.delete(orderId);
        if (removals.size === 0) {
          newMap.delete(taskId);
        } else {
          newMap.set(taskId, removals);
        }
      }
      return newMap;
    });
  };

  // Check if there are pending changes for a task
  const hasPendingChanges = (taskId: string) => {
    const removals = pendingRemovals.get(taskId);
    return removals ? removals.size > 0 : false;
  };

  // Save pending changes for a task
  const handleSaveOrderChanges = async (taskId: string) => {
    const removals = pendingRemovals.get(taskId);
    if (!removals || removals.size === 0) return;

    const task = tasks.find((t) => t.id === taskId);
    if (!task) return;

    const currentOrders = taskOrders.get(taskId) || task.orders || [];
    const ordersToKeep = currentOrders
      .filter((order) => !removals.has(order.order_id))
      .map((order) => order.order_id);

    await updateTask(taskId, {
      orders: ordersToKeep,
    });
    // Clear pending removals and refresh orders
    setPendingRemovals((prev) => {
      const newMap = new Map(prev);
      newMap.delete(taskId);
      return newMap;
    });
    // Clear cached orders so they'll be refetched
    setTaskOrders((prev) => {
      const newMap = new Map(prev);
      newMap.delete(taskId);
      return newMap;
    });
    // Refetch orders with loading indicator
    setLoadingOrdersTaskId(taskId);
    try {
      const fullTask = await getTask(taskId);
      if (fullTask && fullTask.orders) {
        setTaskOrders((prev) => new Map(prev).set(taskId, fullTask.orders));
      }
    } catch (error) {
      console.error("Failed to refetch task orders", error);
    } finally {
      setLoadingOrdersTaskId(null);
    }
    // Call the callback to refetch orders and branch stats on the left
    if (onOrdersRemovedFromTask) {
      onOrdersRemovedFromTask();
    }
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 2,
      }}
    >
      {/* Header + create button */}
      <Stack direction="row" alignItems="center" spacing={1}>
        <LocalShippingIcon fontSize="small" />
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          {transferType === "Inbound" ? "Incoming transfer tasks" : "Outbound transfer tasks"}
        </Typography>
        {transferType === "Outbound" && (
          <Tooltip
            title={
              "Create a new empty task. Orders can be added via drag and drop."
            }
          >
            <span>
              <Button
                size="small"
                variant="contained"
                startIcon={isCreatingTask ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                disabled={isLoading || isCreatingTask || !selectedBranch || isPastFilterSelected}
                onClick={handleCreateTask}
              >
                {isCreatingTask ? "Creating..." : "New Task"}
              </Button>
            </span>
          </Tooltip>
        )}
      </Stack>

      <Divider />

      {/* Filters section */}
      {!isLoading && (
        <Box sx={{ mb: 1, display: "flex", flexDirection: "row", gap: 2, alignItems: "flex-start", flexWrap: "wrap" }}>
          {/* Filter chips for Active/Past tasks */}
          <Box sx={{ flex: 1, minWidth: 200 }}>
            <FilterChips<TaskFilter>
              label=""
              options={["Active", "Past"]}
              multiple={false}
              selected={selectedFilter}
              onChange={(selected) => {
                // Ensure at least one filter is always selected
                if (selected.length === 0) {
                  setSelectedFilter(["Active"]);
                } else {
                  setSelectedFilter(selected as TaskFilter[]);
                }
              }}
              getOptionLabel={(option) => option}
              optionsEqual={(opt1, opt2) => opt1 === opt2}
            />
          </Box>
          
          {/* Driver filter */}
          <Box sx={{ minWidth: 250, maxWidth: 300 }}>
            <DriverSelector
              selectedDriver={selectedDriverFilter}
              onSelect={setSelectedDriverFilter}
              label="Filter by Driver"
              required={false}
              disabled={isLoading}
              placeholder="Select driver to filter tasks"
              compact
            />
          </Box>
        </Box>
      )}

        {
        isLoading ? <CircularProgress /> : <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          mt: 1,
          alignItems: "center",
        }}
      >
        {selectedCount > 0 && transferType === "Outbound" && (
          <Typography variant="body2" color="text.secondary">
            Drag basket from the left and add it to the proper transfer task.
          </Typography>
        )}
        
        {transferType === "Inbound" && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Viewing transfer tasks incoming to {currentBranch?.name || "current branch"}
            {selectedBranch && ` from ${selectedBranch.name}`}
          </Typography>
        )}

        {tasks.map((task) => {
          const isDragOver = dragOverTaskId === task.id;
          const isUpdating = updatingTaskId === task.id;
          const canModify = transferType === "Outbound"; // Only allow modifications in Outbound mode

          return (
            <Card
              key={task.id}
              sx={{
                width: "100%",
                maxWidth: 600,
                borderRadius: 3,
                borderWidth: 2,
                borderStyle: "solid",
                borderColor: isDragOver ? "primary.main" : "divider",
                backgroundColor: isDragOver
                  ? "action.selected"
                  : "background.paper",
                boxShadow: isDragOver 
                  ? "0 8px 24px rgba(25, 118, 210, 0.2)" 
                  : "0 2px 8px rgba(0, 0, 0, 0.08)",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                transform: isDragOver ? "translateY(-4px) scale(1.01)" : "translateY(0) scale(1)",
                opacity: isUpdating ? 0.6 : 1,
                position: "relative",
                overflow: "visible",
                "&:hover": {
                  boxShadow: isDragOver 
                    ? "0 8px 24px rgba(25, 118, 210, 0.2)" 
                    : "0 4px 12px rgba(0, 0, 0, 0.12)",
                  borderColor: isDragOver ? "primary.main" : "primary.light",
                },
              }}
              onDragOver={isUpdating || !canModify ? undefined : handleAllowDrop}
              onDragEnter={isUpdating || !canModify ? undefined : (e) => handleDragEnterTask(e, task.id)}
              onDragLeave={isUpdating || !canModify ? undefined : (e) => handleDragLeaveTask(e, task.id)}
              onDrop={isUpdating || !canModify ? undefined : (e) => handleDropOnTask(e, task.id)}
            >
              <CardHeader
                title={
                  <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
                    <Typography variant="body1" fontWeight={600} sx={{ fontSize: '1rem' }}>
                      Transfer #{task.id}
                    </Typography>
                    <Chip
                      size="small"
                      label={`${task.orders?.length || 0} orders`}
                      color="primary"
                      sx={{ height: 22, fontSize: '0.7rem', fontWeight: 500 }}
                    />
                  </Stack>
                }
                subheader={
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mt: 1 }} flexWrap="wrap">
                    {task.source_branch && task.target_branch && (
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ 
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5
                        }}
                      >
                        <Box component="span" sx={{ fontWeight: 600 }}>{task.source_branch.name}</Box>
                        <Box component="span">→</Box>
                        <Box component="span" sx={{ fontWeight: 600 }}>{task.target_branch.name}</Box>
                      </Typography>
                    )}
                    <Chip
                      size="small"
                      label={task.status}
                      color={
                        task.status === "COMPLETED"
                          ? "success"
                          : task.status === "FAILED" || task.status === "CANCELLED"
                          ? "error"
                          : task.status === "IN_PROGRESS"
                          ? "warning"
                          : "default"
                      }
                      sx={{ height: 22, fontSize: '0.7rem', fontWeight: 500 }}
                    />
                  </Stack>
                }
                action={
                  canModify && (
                    <Tooltip title="Delete task">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteTask(task.id)}
                        disabled={isLoading || deletingTaskId === task.id || isUpdating}
                        sx={{ 
                          padding: 0.75,
                          "&:hover": {
                            backgroundColor: "error.light",
                            color: "error.main",
                          }
                        }}
                      >
                        {deletingTaskId === task.id ? (
                          <CircularProgress size={18} color="inherit" />
                        ) : (
                          <DeleteIcon fontSize="small" />
                        )}
                      </IconButton>
                    </Tooltip>
                  )
                }
                sx={{
                  padding: 2,
                  paddingBottom: 1.5,
                  "& .MuiCardHeader-title": {
                    fontSize: '1rem',
                    marginBottom: 0,
                  },
                  "& .MuiCardHeader-subheader": {
                    fontSize: '0.75rem',
                    marginTop: 0.5,
                  },
                }}
              />
              {canAssignDriver(task) && canModify && (
                <CardContent sx={{ padding: 2, paddingTop: 0, paddingBottom: 1.5 }}>
                  <Box>
                    <DriverSelector
                      selectedDriver={task.driver}
                      onSelect={(driver) => handleAssignDriver(task.id, driver)}
                      label="Assign Driver"
                      required={false}
                      disabled={isLoading || assigningDriverTaskId === task.id || isUpdating}
                      placeholder="Search driver by name or phone"
                      compact
                    />
                  </Box>
                  
                  {assigningDriverTaskId === task.id && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
                      <CircularProgress size={16} />
                      <Typography variant="caption" color="text.secondary">
                        Assigning driver...
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              )}
              {!canAssignDriver(task) && task.driver && (
                <CardContent sx={{ padding: 2, paddingTop: 0, paddingBottom: 1.5 }}>
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    p: 1,
                    borderRadius: 1,
                    backgroundColor: "action.hover"
                  }}>
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Driver:
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {task.driver.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ({task.driver.phone_number})
                    </Typography>
                  </Box>
                </CardContent>
              )}
              
              {/* Receive button for inbound active tasks */}
              <CardContent sx={{ padding: 2, paddingTop: 0, paddingBottom: 1.5 }}>
                <Button
                  size="medium"
                  variant="contained"
                  color="primary"
                  startIcon={<AssignmentTurnedInIcon />}
                  onClick={() => setReceiveDialogTaskId(task.id)}
                  sx={{
                    minWidth: 160,
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: 2,
                    boxShadow: 2,
                    "&:hover": {
                      boxShadow: 4,
                    },
                  }}
                >
                  Receive Transfer
                </Button>
              </CardContent>
              
              
              {/* Orders section */}
              <CardContent sx={{ padding: 2, paddingTop: 0 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                    p: 1,
                    borderRadius: 1,
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                    transition: "background-color 0.2s ease",
                  }}
                  onClick={() => handleToggleOrders(task.id)}
                >
                  <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.875rem' }}>
                    Orders ({task.orders?.length || 0})
                  </Typography>
                  <IconButton 
                    size="small" 
                    sx={{ 
                      padding: 0.5,
                      "&:hover": {
                        backgroundColor: "action.selected",
                      }
                    }}
                  >
                    {expandedTaskId === task.id ? (
                      <ExpandLessIcon fontSize="small" />
                    ) : (
                      <ExpandMoreIcon fontSize="small" />
                    )}
                  </IconButton>
                </Box>
                <Collapse in={expandedTaskId === task.id}>
                  <Box sx={{ mt: 1 }}>
                    {loadingOrdersTaskId === task.id ? (
                      <Box sx={{ display: "flex", justifyContent: "center", p: 2 }}>
                        <CircularProgress size={24} />
                      </Box>
                    ) : (
                      <>
                        {hasPendingChanges(task.id) && canModify && (
                          <Box sx={{ mb: 1.5, display: "flex", justifyContent: "flex-end" }}>
                            <Button
                              size="small"
                              variant="contained"
                              startIcon={<SaveIcon />}
                              onClick={() => handleSaveOrderChanges(task.id)}
                              disabled={updatingTaskId === task.id}
                              sx={{
                                textTransform: "none",
                                fontWeight: 600,
                                borderRadius: 2,
                                minWidth: 140,
                              }}
                            >
                              Save Changes
                            </Button>
                          </Box>
                        )}
                        {taskOrders.get(task.id)?.length ? (
                          <Box>
                            <List dense>
                              {taskOrders.get(task.id)?.map((order) => {
                                const isMarkedForRemoval = pendingRemovals.get(task.id)?.has(order.order_id) || false;
                                return (
                                  <ListItem
                                    key={order.order_id}
                                    sx={{
                                      borderBottom: "1px solid",
                                      borderColor: "divider",
                                      py: 0.5,
                                      px: 1,
                                      opacity: isMarkedForRemoval ? 0.5 : 1,
                                      backgroundColor: isMarkedForRemoval ? "action.hover" : "transparent",
                                    }}
                                    secondaryAction={
                                      canModify ? (
                                        <IconButton
                                          edge="end"
                                          size="small"
                                          onClick={() => {
                                            if (isMarkedForRemoval) {
                                              handleAddBackOrder(task.id, order.order_id);
                                            } else {
                                              handleRemoveOrder(task.id, order.order_id);
                                            }
                                          }}
                                          sx={{ padding: 0.5 }}
                                        >
                                          {isMarkedForRemoval ? (
                                            <Tooltip title="Add back">
                                              <RestoreIcon fontSize="small" color="primary" />
                                            </Tooltip>
                                          ) : (
                                            <Tooltip title="Remove">
                                              <RemoveCircleOutlineIcon fontSize="small" color="error" />
                                            </Tooltip>
                                          )}
                                        </IconButton>
                                      ) : undefined
                                    }
                                  >
                                    <Box sx={{ width: "100%", pr: 4 }}>
                                      <Stack spacing={0.5}>
                                        <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
                                          <Typography variant="body2" fontWeight={600} sx={{ minWidth: "fit-content" }}>
                                            #{order.order_id}
                                          </Typography>
                                          {isMarkedForRemoval && (
                                            <Chip
                                              size="small"
                                              label="Removed"
                                              color="error"
                                              sx={{ height: 18, fontSize: '0.65rem' }}
                                            />
                                          )}
                                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                                            {showWorkflow(
                                              order.workflow,
                                              order.is_exchange,
                                              order.exchange_for,
                                              order.return_reason
                                            )}
                                          </Box>
                                        </Stack>
                                        <Stack direction="row" spacing={1.5} alignItems="flex-start" flexWrap="wrap">
                                          {order.merchant && (
                                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                                              <a href={`/merchant/${order.merchant.id}`} style={{ textDecoration: 'none' }}>
                                                <MerchantWithIcon merchant={order.merchant} />
                                              </a>
                                              {order.merchant_location && (
                                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', ml: 3 }}>
                                                  {displayLocation(order.merchant_location)}
                                                </Typography>
                                              )}
                                            </Box>
                                          )}
                                          {order.customer && (
                                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                                              <CustomerWithNavigate customer={order.customer} location={order.customer_location} />
                                            </Box>
                                          )}
                                        </Stack>
                                      </Stack>
                                    </Box>
                                  </ListItem>
                                );
                              })}
                            </List>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: "center" }}>
                            No orders in this task
                          </Typography>
                        )}
                      </>
                    )}
                  </Box>
                </Collapse>
              </CardContent>
              
              {isUpdating && (
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    backgroundColor: "rgba(255, 255, 255, 0.95)",
                    backdropFilter: "blur(2px)",
                    borderRadius: 3,
                    zIndex: 10,
                    border: "2px solid",
                    borderColor: "primary.light",
                  }}
                >
                  <CircularProgress size={40} thickness={4} />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2, fontWeight: 500 }}>
                    Saving...
                  </Typography>
                </Box>
              )}
            </Card>
          );
        })}
      </Box>
      }
      
      {/* Receive Transfer Dialog */}
      {receiveDialogTaskId && (() => {
        const task = tasks.find((t) => t.id === receiveDialogTaskId);
        if (!task) return null;
        return (
          <ReceiveTransferDialog
            task={task}
            open={true}
            onClose={() => setReceiveDialogTaskId(null)}
            onSuccess={() => {
              // Refetch tasks after successful receive
              setReceiveDialogTaskId(null);
              // The tasks will automatically refetch due to the hook
            }}
          />
        );
      })()}
    </Box>
  );
};
